import { TransactionsLists } from 'vopay';
import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import InteracBulkPayoutTransactionInfo from './InteracBulkPayoutTransactionInfo';
import useFetchInteracBulkPayoutTransaction from '@/hooks/useApi/useFetchInteracBulkPayoutTransaction';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type TransactionProps = {
  TransactionID: string;
  notes?: string;
  isParent?: boolean;
  jwt: string;
  eLinxRequestID?: string;
  // InteracBulkPayoutTransactionResponse has no parent-linking field of its own, so
  // TransactionDetails.tsx resolves the parent generically by ID and passes it down — shared with
  // FundingStatusBanner instead of this container fetching its own copy.
  parentTransaction?: TransactionsLists;
} & Omit<
  ComponentProps<typeof InteracBulkPayoutTransactionInfo>,
  'transaction' | 'Notes' | 'isParent' | 'ELinxRequestID' | 'parentTransactionId' | 'parentTransactionStatus' | 'parentType'
>;

export default function InteracBulkPayoutTransactionContainer({
  TransactionID,
  notes,
  isParent = false,
  jwt,
  eLinxRequestID = '',
  parentTransaction,
  ...props
}: TransactionProps) {
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { interacBulkPayoutTransaction, fetchInteracBulkPayoutTransaction } = useFetchInteracBulkPayoutTransaction({
    bearer: jwt,
  });

  useEffect(() => {
    blockingLoader.show();

    fetchInteracBulkPayoutTransaction(TransactionID)
      .catch((error) =>
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred')),
      )
      .finally(() => blockingLoader.hide());
  }, [TransactionID]);

  const transaction = interacBulkPayoutTransaction;

  return (
    <div>
      {transaction && (
        <InteracBulkPayoutTransactionInfo
          transaction={transaction}
          Notes={notes || ''}
          isParent={isParent}
          jwt={jwt}
          ELinxRequestID={eLinxRequestID}
          parentTransactionId={parentTransaction?.TransactionID}
          parentTransactionStatus={parentTransaction?.TransactionStatus}
          parentType={parentTransaction?.TransactionType}
          {...props}
        />
      )}
    </div>
  );
}
